<template>
    <ag-grid-vue
        class="w-full h-full min-w-[830px] ag-theme-balham"
        :class="{ 'h-full': transactionsWithLines.length > 10, 'h-1/3': transactionsWithLines.length <= 10 }"
        :row-data="transactionsWithLines"
        :column-defs="columns"
        :grid-options="gridOptions"
        :theme="agTheme"
    />
</template>

<script setup lang="ts">

import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {computed, inject, Ref, shallowRef} from 'vue'
import {Transaction} from '@/types/transaction/Transaction'
import {CellClassParams, ColDef, ColSpanParams, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, RowClassParams, ValueGetterParams} from 'ag-grid-community'
import TransactionJournalNameCell from '@/transaction-journal/TransactionJournalNameCell.vue'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import TransactionJournalAccountCell from '@/transaction-journal/TransactionJournalAccountCell.vue'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {useVatService} from '@/vat/vat.service'
import TransactionJournalEntryActions from '@/transaction-journal/TransactionJournalEntryActions.vue'
import noRowsOverlayComponent from '@/components/NoRowsOverlayComponent.vue'
import TransactionJournalHeadDescriptionCell from '@/transaction-journal/TransactionJournalHeadDescriptionCell.vue'
import TransactionJournalDetailsRow from '@/transaction-journal/TransactionJournalDetailsRow.vue'
import {v4 as uuidv4} from 'uuid'
import {globalDefaultColDef} from '@/agUtils'
import {useDimensions} from '@/dimensions/dimensions.service'

const gridApi = shallowRef<GridApi>()
const listId = uuidv4()
const editClone = inject<Function | undefined>('editClone', undefined)
const editExisting = inject<Function | undefined>('editExisting', undefined)
const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<number> | undefined = inject('panelPathSegment')
const {getVatOptionById} = useVatService()

enum RowType {'T', 'TL', 'D'}

type TransactionJournalGridLine = (Transaction | TransactionLine) & { type: RowType }

const props = defineProps({
    transactions: {type: Array<Transaction>, default: () => []},
    loading: {type: Boolean, default: false},
    sortByCreatedAt: {type: Boolean, default: false}
})

const {projects, departments} = useDimensions()


const transactionsWithLines = computed(() => {
    const sorted = props.transactions.map(transaction => {
        return {
            ...transaction,
            domId: `trans_${listId}_${transaction.uuid}`,
            type: RowType.T
        }
    })

    const rows = []
    for (const transaction of sorted) {
        rows.push(transaction)
        for (let i = 0; i < transaction.transactionLines.length; i++) {
            const tl = {
                ...transaction.transactionLines[i],
                type: RowType.TL,
                transactionName: ''
            }


            if (!tl.corrected && !tl.correction && (!tl.deleted || transaction.deleted)) {
                rows.push(tl)
            }
        }
        const detailsLine = {
            transactionUuid: transaction.uuid,
            uuid: transaction.uuid + '_details',
            type: RowType.D,
            attachments: transaction.documentMetaUuids,
        }
        rows.push(detailsLine)
    }

    return rows
})

const columns = computed<ColDef[]>(() => [
    {
        headerName: 'Bilagsnr.',
        field: '',
        width: 115,
        cellRendererSelector: ({data}: ICellRendererParams) => {
            if (data.type === RowType.T) {
                return {component: TransactionJournalNameCell}
            } else if (data.type === RowType.TL) {
                return {component: () => formattedDate(data.date)}
            }
        },
        cellClass: ({data}: CellClassParams) => {
            if (data.type === RowType.T) return 'text-[16px] pt-1'
            return 'ml-2'
        }
    },
    {
        headerName: 'Beskrivelse',
        flex: 1,
        valueGetter: ({data}: ValueGetterParams) => {
            return data
        },
        cellRendererSelector: ({data}: ICellRendererParams) => {
            if (data.type === RowType.T) {
                return {component: TransactionJournalHeadDescriptionCell}
            } else if (data.type === RowType.TL) {
                return {component: () => data.description}
            } else if (data.type === RowType.D) {
                return {component: TransactionJournalDetailsRow}
            }
        },
        colSpan: ({data}: ColSpanParams) => {
            if (data.type === RowType.T) return 4
            if (data.type === RowType.D) return 6
            return 1
        },
        cellClass: ({data}: CellClassParams) => {
            if (data.type === RowType.T) return 'text-[16px] mt-[5px]'
        }
    },
    {
        headerName: 'Konto / kunde / lev.',
        field: '',
        flex: 1,
        cellRendererSelector: ({data}: ICellRendererParams) => {
            if (data.type === RowType.TL) {
                return {component: TransactionJournalAccountCell}
            }
        }
    },
    {
        headerName: 'MVA',
        width: 60,
        field: 'vatOptionId',
        valueGetter: ({data}: ValueGetterParams) => {
            return getVatOptionById(data.vatOptionId)?.code || 0
        },
        cellClassRules: {
            'opacity-20': (params: CellClassParams) => params.value === 0
        }
    },
    {
        headerName: 'Prosjekt',
        width: 100,
        field: 'project',
        hide: !projects.value.length
    },
    {
        headerName: 'Avdeling',
        width: 100,
        field: 'department',
        hide: !departments.value.length
    },
    {
        headerName: 'Beløp',
        valueGetter: (x : ValueGetterParams) => {
            // here lies dragons!
            if (x.data.type === RowType.T) {
                return x.data.transactionLines?.length
            } else if (x.data.type === RowType.TL) {
                return x.data.amount
            }
        },
        headerClass: 'header-right',
        pinned: 'right',
        field: 'amount',//remarks: needed for cypress
        cellRendererSelector: ({data}: ICellRendererParams) => {
            if (data.type === RowType.T) {
                return {component: TransactionJournalEntryActions}
            } else if (data.type === RowType.TL) {
                return {component: () => formatCurrency(data.amount)}
            }
        },
        cellClass: 'text-right'
    }
])

const gridOptions: GridOptions = {
    enableCellTextSelection: true,
    suppressContextMenu: true,
    getRowHeight: ({data}: { data: TransactionJournalGridLine }) => {
        if (data.type === RowType.T) {
            return 45
        } else {
            return 30
        }
    },
    rowClass: 'tranjou-row',
    rowClassRules: {
        'tranjou-details-row': (params: RowClassParams) => params.data.type === RowType.D
    },
    defaultColDef: {
        ...globalDefaultColDef,
        headerName: '',
        resizable: true,
        sortable: false,
        filter: false
    },
    noRowsOverlayComponent: noRowsOverlayComponent,
    noRowsOverlayComponentParams: {
        getMessage: () => props.loading ? 'Laster....' : 'Ingen bilag i perioden'
    },
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
    },
    getRowId: (node: { data: { uuid: string } }) => {
        return node.data.uuid
    },
    context: {
        editClone,
        editExisting,
        panelId,
        panelPathSegment
    }
}
</script>

<style>
</style>